import { Box, Typography } from "@material-ui/core";
import { D, pipe } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import { SignType } from "../../../domain";
import {
  createParser,
  useStateFromParams,
} from "../../../hooks/useStateFromParams";
import { GridPaginator } from "../../../theme";
import SignedTypeSelect from "../../SignedTypeSelect";
import SignerSelect from "../../SignerSelect";
import ProductContainer from "./ProductContainer";

function toMap(acc, value) {
  acc[value] = true;
  return acc;
}

const parsers = pipe(
  {},
  D.merge(
    createParser({
      name: "signedTypes",
      key: "st",
      getter: (paramValue) =>
        paramValue
          ?.split?.(",")
          .filter(SignType.enum.has.bind(SignType.enum))
          .reduce(toMap, {}) ?? {},
    }),
  ),
  D.merge(
    createParser({
      name: "signers",
      key: "si",
      getter: (paramValue) =>
        paramValue?.split?.(",").filter(Boolean).reduce(toMap, {}) ?? {},
    }),
  ),
);

const ShopProducts = React.memo(function ShopProducts({
  influencer,
  setProduct,
}) {
  const [state, setState] = useStateFromParams(parsers);

  const { distinctSigners, distinctSignedTypes } = React.useMemo(() => {
    if (!influencer?.products) {
      return { distinctSigners: [], distinctSignedTypes: [] };
    }

    return influencer.products.reduce(
      (result, product) => {
        const { fulfillers, signedType } = product;

        result.distinctSignedTypes.add(signedType);

        if (!Array.isArray(fulfillers)) {
          return result;
        }

        fulfillers.forEach((fulfiller) => {
          if (!result.distinctSigners[fulfiller.influencerId]) {
            result.distinctSigners[fulfiller.influencerId] = {
              influencerId: fulfiller.influencerId,
              name: fulfiller.name,
              avatarUrl: fulfiller.avatarUrl,
            };
          }
        });

        return result;
      },
      { distinctSignedTypes: new Set(), distinctSigners: {} },
    );
  }, [influencer.products]);

  const products = React.useMemo(() => {
    if (!influencer?.products) {
      return [];
    }

    const hasFilters =
      !D.isEmpty(state.signedTypes) || !D.isEmpty(state.signers);

    if (!hasFilters) {
      return influencer.products.map((product) => (
        <ProductContainer
          key={product.productId}
          product={product}
          onOpen={setProduct}
          influencer={influencer}
        />
      ));
    }

    const results = influencer.products.reduce((result, product) => {
      const notFilteringBySignedType = D.isEmpty(state.signedTypes);
      const notFilteringBySigner = D.isEmpty(state.signers);

      const hasSignedType = Boolean(state.signedTypes[product.signedType]);
      const hasSigner = Boolean(
        product.fulfillers?.some?.((fulfiller) =>
          Boolean(state.signers[fulfiller.influencerId]),
        ),
      );

      if (
        (notFilteringBySignedType || hasSignedType) &&
        (notFilteringBySigner || hasSigner)
      ) {
        result.push(
          <ProductContainer
            key={product.productId}
            product={product}
            onOpen={setProduct}
            influencer={influencer}
          />,
        );
      }
      return result;
    }, []);

    // Reset if no products found
    if (results.length === 0) {
      setState(D.merge({ signedTypes: {}, signers: {} }));
      return influencer.products.map((product) => (
        <ProductContainer
          key={product.productId}
          product={product}
          onOpen={setProduct}
          influencer={influencer}
        />
      ));
    }

    return results;
  }, [influencer, state, setProduct, setState]);

  const setSigners = React.useCallback(
    (signers) => {
      setState(D.merge({ signers }));
    },
    [setState],
  );

  const setSignedTypes = React.useCallback(
    (signedTypes) => {
      setState(D.merge({ signedTypes }));
    },
    [setState],
  );

  const signedTypeOptions = React.useMemo(() => {
    if (distinctSignedTypes) {
      return Array.from(distinctSignedTypes).sort();
    }
  }, [distinctSignedTypes]);

  const hasFilters =
    (distinctSigners && Object.keys(distinctSigners).length > 1) ||
    (signedTypeOptions && Object.keys(signedTypeOptions).length > 1);

  return (
    <Box pb={8} pt={4}>
      <Box mb={4}>
        {hasFilters ? (
          <Typography gutterBottom variant="subtitle2">
            Filter Products
          </Typography>
        ) : null}

        <SignerSelect
          initialState={state.signers}
          onChange={setSigners}
          options={distinctSigners}
        />

        <SignedTypeSelect
          options={signedTypeOptions}
          initialState={state.signedTypes}
          onChange={setSignedTypes}
        />
      </Box>

      {products.length > 0 && (
        <GridPaginator useObserver pageItems={products} pageSize={20} />
      )}
    </Box>
  );
});

ShopProducts.propTypes = {
  signType: PropTypes.string,
  setProduct: PropTypes.func,
};

export default ShopProducts;
